<template>
  <v-container fill-height>
    <v-row v-if="resultadosPersonal.length == 0" justify="center">
      <v-col cols="4">
        <v-card class="card" color="primary">
          <v-card-title class="justify-center font-weight-bold">Buscar personal</v-card-title>
          <v-card-text>
            <BaseInput v-model="busquedaPersonal.nombre" label="Nombre" />
            <BaseInput v-model="busquedaPersonal.segundoNombre" label="Segundo nombre" />
            <BaseInput v-model="busquedaPersonal.apaterno" label="Apellido paterno" />
            <BaseInput v-model="busquedaPersonal.amaterno" label="Apellido materno" />
            <BaseInput v-model="busquedaPersonal.curp" label="CURP" />
            <BaseInput v-model="busquedaPersonal.CUIP" label="CUIP" />
          </v-card-text>
          <v-card-actions class="justify-center pb-8">
            <v-container class="py-0">
              <v-row justify="center">
                <v-col cols="8" align="center">
                  <TextButton @click="triggerSearchPersonal()" :disabled="loading || emptyForm" :loading="loading">
                    Realizar búsqueda
                  </TextButton>
                </v-col>
              </v-row>
            </v-container>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>
    <v-row v-else>
      <v-col cols="12">
        <v-row>
          <v-col cols="4">
            <TextButton @click="RESET_BUSQUEDA_PERSONAL()">Realizar nueva búsqueda</TextButton>
          </v-col>
          <v-spacer></v-spacer>
          <v-col cols="4">
            <BaseInput label="Filtrar elementos" v-model="filtroPersonal"></BaseInput>
          </v-col>
        </v-row>
      </v-col>
      <v-col cols="12" lg="4" xl="3" v-for="personal in filteredPersonal" :key="personal.id">
        <v-card class="card" color="secondary" elevation="8">
          <v-card-title>
            <v-container>
              <v-row>
                <v-col cols="12" align="center">
                  <v-avatar rounded color="primary" size="80" v-if="!hasFoto(personal) || personal.fotoError">
                    <v-icon x-large color="secondary">mdi-account</v-icon>
                  </v-avatar>
                  <v-img
                    v-else
                    :src="personal.foto"
                    max-height="80"
                    max-width="80"
                    @error="personal.fotoError = true"
                  />
                </v-col>

                <v-col cols="12" align="center">
                  <div class="text-h6 primary--text font-weight-bold">{{ personal.apaterno }}</div>
                  <div class="text-subtitle-1 primary--text">
                    {{ personal.amaterno + ' ' + personal.nombre + ' ' + personal.segundoNombre }}
                  </div>
                </v-col>
              </v-row>
            </v-container>
          </v-card-title>
          <v-card-text>
            <v-container>
              <v-row>
                <v-col cols="12">
                  <div class="text-caption primary--text">Evaluación C3 vigente hasta</div>
                  <div
                    v-if="personal.examenC3Mes.length > 0 && personal.vigenciaAnio != 0"
                    class="text-subtitle-1 primary--text"
                  >
                    {{ personal.examenC3Mes }} del {{ personal.vigenciaAnio }}
                  </div>
                  <div v-else class="text-subtitle-1 primary--text">Información no registrada</div>
                </v-col>
                <v-col cols="12">
                  <div class="text-caption primary--text">Competencias básicas</div>
                  <div v-if="personal.vigenciaCompetenciasBasicas" class="text-subtitle-1 primary--text">
                    <v-row align="center" no-gutters>
                      <v-col class="pa-0" cols="7">
                        {{ formatDateAjustada(personal.vigenciaCompetenciasBasicas) }}
                      </v-col>
                      <v-col class="pa-0" cols="5" align="right">
                        <Semaforo
                          :meses="getMesesDesdeVigencia(personal.vigenciaCompetenciasBasicas)"
                          tipoRegla="profesionalizacion"
                          nombreVigencia="Competencias básicas"
                          :fechaVigencia="getFechaVigenciaAjustada(personal.vigenciaCompetenciasBasicas)"
                        />
                      </v-col>
                    </v-row>
                  </div>
                  <div v-else class="text-subtitle-1 primary--text" align="left">Información no registrada</div>
                </v-col>
              </v-row>
            </v-container>
          </v-card-text>
          <v-card-actions class="secondary darken-1">
            <v-container>
              <v-row>
                <v-col cols="12" align="right">
                  <v-tooltip bottom color="black">
                    <template v-slot:activator="{ on, attrs }">
                      <IconRoundedButton
                        class="mr-1"
                        elevation="8"
                        :to="{
                          name: 'profesionalizacion.evaluacion_desempenio_academico',
                          params: { id: personal.idPersonal },
                        }"
                        icon="mdi-school"
                        v-on="on"
                        v-bind="attrs"
                      />
                    </template>
                    <span>Realizar evaluación de desempeño académico</span>
                  </v-tooltip>
                  <v-tooltip bottom color="black">
                    <template v-slot:activator="{ on, attrs }">
                      <IconRoundedButton
                        :elevation="8"
                        :to="{
                          name: 'profesionalizacion.expedientePersonalSection',
                          params: { id: personal.idPersonal },
                        }"
                        icon="mdi-file-account"
                        v-on="on"
                        v-bind="attrs"
                      >
                      </IconRoundedButton>
                    </template>
                    <span>Ver expediente del personal</span>
                  </v-tooltip>
                </v-col>
              </v-row>
            </v-container>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import _ from 'lodash'
import moment from 'moment'
import BaseInput from '@src/components/base/BaseInput.vue'
import IconRoundedButton from '@src/components/base/IconRoundedButton.vue'
import TextButton from '@src/components/base/TextButton.vue'
import { mapActions, mapGetters, mapMutations } from 'vuex'
import Semaforo from '@/components/SemaforoComponent.vue'

export default {
  components: {
    BaseInput,
    TextButton,
    IconRoundedButton,
    Semaforo,
  },
  data() {
    return {
      loading: false,
      filtroPersonal: '',
      selectedPersonal: null,
    }
  },
  computed: {
    ...mapGetters({
      perfil: 'getPerfil',
    }),

    ...mapGetters('personal', {
      resultadosPersonal: 'getPersonal',
      busquedaPersonal: 'getBusquedaPersonal',
    }),

    filteredPersonal: function () {
      return this.resultadosPersonal.filter((personal) => {
        return (
          this.getFullName(personal).toLowerCase().indexOf(this.filtroPersonal.toLowerCase()) >= 0 ||
          personal.CUIP.toLowerCase().indexOf(this.filtroPersonal.toLowerCase()) >= 0 ||
          personal.rfc.toLowerCase().indexOf(this.filtroPersonal.toLowerCase()) >= 0 ||
          personal.curp.toLowerCase().indexOf(this.filtroPersonal.toLowerCase()) >= 0
        )
      })
    },

    emptyForm() {
      if (
        this.busquedaPersonal.nombre === '' &&
        this.busquedaPersonal.segundoNombre === '' &&
        this.busquedaPersonal.apaterno === '' &&
        this.busquedaPersonal.amaterno === '' &&
        this.busquedaPersonal.curp === '' &&
        this.busquedaPersonal.CUIP === ''
      ) {
        return true
      } else return false
    },
  },
  methods: {
    ...mapActions('personal', ['searchPersonal']),
    ...mapMutations(['SET_SNACKBAR_MESSAGE']),
    ...mapMutations('personal', ['RESET_BUSQUEDA_PERSONAL']),

    async triggerSearchPersonal() {
      this.loading = true

      await this.searchPersonal(this.busquedaPersonal)
      if (this.resultadosPersonal.length == 0) {
        this.SET_SNACKBAR_MESSAGE('Búsqueda sin resultados.')
      }

      this.loading = false
    },
    hasFoto(personal) {
      const urlSinFoto =
        'https://edofza.eoninnovaction.com/CICG_SEG/ESTADO_FUERZA/Files/Fotografia/frente_AARD930724H214693851.jpg'
      return (
        personal.foto &&
        typeof personal.foto === 'string' &&
        personal.foto.trim() !== '' &&
        personal.foto !== urlSinFoto
      )
    },
    getFullName(personal) {
      return `${personal.apaterno} ${personal.amaterno} ${personal.nombre} ${personal.segundoNombre || ''}`.trim()
    },
    getMesesDesdeVigencia(fechaVigencia) {
      const fechaAjustada = this.getFechaVigenciaAjustada(fechaVigencia)
      if (!fechaAjustada) return null
      const fechaMoment = moment(fechaAjustada, 'YYYY-MM-DD', true)
      if (!fechaMoment.isValid()) return null
      const meses = fechaMoment.diff(moment(), 'months', true)
      return meses >= 0 ? meses : 0
    },
    formatDateAjustada(date) {
      const fechaAjustada = this.getFechaVigenciaAjustada(date)
      if (!fechaAjustada) return 'Sin fecha'
      return moment(fechaAjustada, 'YYYY-MM-DD').format('DD/MM/YYYY')
    },
    getFechaVigenciaAjustada(fecha) {
      if (!fecha) return null
      let formato = 'YYYY-MM-DD'
      if (/^\d{2}\/\d{2}\/\d{4}$/.test(fecha)) {
        formato = 'DD/MM/YYYY'
      }
      const fechaMoment = moment(fecha, formato, true)
      if (!fechaMoment.isValid()) return null
      return fechaMoment.add(3, 'years').format('YYYY-MM-DD')
    },
  },
}
</script>

<style scoped>
.fab {
  position: fixed;
  bottom: 24px;
  right: 24px;
}
</style>
